import { useCallback, useRef } from "react";

export function useTripleTouch({
  onTripleTouch,
  holdDuration = 5000,
}: {
  onTripleTouch?: () => void;
  holdDuration?: number;
}) {
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holding = useRef(false);

  const clearHold = useCallback(() => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    holding.current = false;
  }, []);

  const startHold = useCallback(() => {
    if (holding.current) return;
    holding.current = true;
    holdTimer.current = setTimeout(() => {
      holdTimer.current = null;
      holding.current = false;
      onTripleTouch?.();
    }, holdDuration);
  }, [holdDuration, onTripleTouch]);

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      if (event.touches.length === 3) {
        startHold();
        return;
      }
      clearHold();
    },
    [clearHold, startHold]
  );

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      if (event.touches.length !== 3) clearHold();
    },
    [clearHold]
  );

  const handleTouchMove = useCallback((_event: TouchEvent) => {
    // A finger lift cancels the hold in touchend. Movement while three fingers stay down does not.
  }, []);

  const reset = useCallback(() => {
    clearHold();
  }, [clearHold]);

  return { handleTouchStart, handleTouchEnd, handleTouchMove, reset };
}
